import {
	compileForAI,
	compileDesignAssetsForAI,
	copyDesignDefinition,
	createDesignModeValue,
	createDesignDefinition,
	createDesignTokenId,
	DEFAULT_STORY_DESIGN_MODES,
	type DesignDefinition,
} from '../design-tokens';
import type { SupportedLanguage } from '../i18n/types';
import type { BrandKitFixture, BrandLink, StoredBrandKit } from './types';

export function normalizeStoredBrandKit(kit: StoredBrandKit): StoredBrandKit {
	return { ...kit, design: copyDesignDefinition(kit.design) };
}

export function setPrimaryBrandLink(links: BrandLink[], id: string | null): BrandLink[] {
	return links.map(link => ({ ...link, ...(link.id === id ? { isPrimary: true } : { isPrimary: undefined }) }));
}

export function getBrandKitById(kits: StoredBrandKit[] | undefined, id: string | null | undefined) {
	return id ? kits?.find(kit => kit.id === id) : undefined;
}

export function getDefaultBrandKit(kits: StoredBrandKit[] | undefined) {
	if (kits?.length === 1) return kits[0];
	return kits?.find(kit => kit.isDefault);
}

export function setDefaultBrandKit(kits: StoredBrandKit[], id: string): StoredBrandKit[] {
	if (kits.length <= 1) return kits.map(kit => ({ ...kit, isDefault: undefined }));
	return kits.map(kit => ({ ...kit, ...(kit.id === id ? { isDefault: true } : { isDefault: undefined }) }));
}

export function appendBrandKit(kits: StoredBrandKit[], next: StoredBrandKit): StoredBrandKit[] {
	if (kits.length === 1 && !kits[0].isDefault) return [{ ...kits[0], isDefault: true }, next];
	return [...kits, next];
}

export function duplicateBrandKit(kits: StoredBrandKit[], id: string): StoredBrandKit | undefined {
	const source = kits.find(kit => kit.id === id);
	if (!source) return undefined;
	const sourceValues = { ...source };
	delete sourceValues.isDefault;

	const baseName = `${source.name} copy`;
	const names = new Set(kits.map(kit => kit.name));
	let name = baseName;
	let suffix = 2;
	while (names.has(name)) {
		name = `${baseName} ${suffix}`;
		suffix += 1;
	}

	return {
		...sourceValues,
		id: createDesignTokenId(
			'brand-kit',
			kits.map(kit => kit.id)
		),
		name,
		links: source.links.map(link => ({ ...link })),
		design: copyDesignDefinition(source.design),
	};
}

export function removeBrandKit(kits: StoredBrandKit[], id: string): StoredBrandKit[] {
	const remaining = kits.filter(kit => kit.id !== id);
	if (remaining.length === 1) return [{ ...remaining[0], isDefault: undefined }];
	return remaining;
}

export function createStoryBrandKitSnapshot(
	kits: StoredBrandKit[] | undefined,
	brandKitId: string | null,
	templateDesign?: DesignDefinition
) {
	const selected = getBrandKitById(kits, brandKitId);
	return {
		brandKitId: selected?.id ?? null,
		designModes: DEFAULT_STORY_DESIGN_MODES,
		design: selected
			? copyDesignDefinition(selected.design)
			: templateDesign
				? copyDesignDefinition(templateDesign)
				: createDesignDefinition(),
	};
}

export function getBrandKitCreationDefaults(
	kits: StoredBrandKit[] | undefined,
	brandKitId: string | null,
	organization: { language: SupportedLanguage; favicon?: string | null }
) {
	const selected = getBrandKitById(kits, brandKitId);
	if (!selected) return { language: organization.language, favicon: organization.favicon ?? undefined };
	return {
		language: selected.language,
		favicon: selected.design.assets.assets.find(asset => asset.role === 'favicon')?.value.base,
	};
}

export function replaceBrandKitValues(current: StoredBrandKit, fixture: BrandKitFixture): StoredBrandKit {
	return {
		schemaVersion: 1,
		id: current.id,
		name: fixture.organizationName,
		language: fixture.brandKit.language,
		...(current.isDefault === undefined ? {} : { isDefault: current.isDefault }),
		links: fixture.brandKit.links,
		guidelines: fixture.brandKit.guidelines,
		design: copyDesignDefinition({
			...fixture.brandKit.design,
			assets: {
				schemaVersion: 1,
				assets: [
					...fixture.brandKit.design.assets.assets,
					...(fixture.favicon
						? [
								{
									id: 'favicon',
									name: 'Favicon',
									role: 'favicon' as const,
									type: 'image' as const,
									value: createDesignModeValue(fixture.favicon),
									description: 'Brand Kit favicon',
								},
							]
						: []),
				],
			},
		}),
	};
}

export function projectBrandKit(kit: StoredBrandKit | undefined, design?: DesignDefinition) {
	const storyDesign = design ?? createDesignDefinition();
	const projectedDesign = {
		defaultFontFamily: storyDesign.defaults.root.fontFamily,
		modeGroups: storyDesign.modeGroups,
		tokens: compileForAI(storyDesign.tokens),
		assets: compileDesignAssetsForAI(storyDesign),
	};
	return kit
		? {
				name: kit.name,
				...projectedDesign,
				links: kit.links,
				guidelines: kit.guidelines,
			}
		: projectedDesign;
}
